import {
  Skeleton,
  Box,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import RailzConnect from '@railzai/railz-connect';
import * as Sentry from '@sentry/react';
import React, { useRef, useEffect } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import {
  typeguards as finleyTypeguards,
  NodeEnvs,
} from '@finley-technologies/finley-types';

import { useGetOrganizationQuery } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  useCreateRailzIntegrationMutation,
  GetActiveRailzConnectionsByOrgIdDocumentGQLType,
} from '../../requests/financialData.gql.generated';
import { FiPlus } from 'react-icons/fi';

/**
 * These Railz Connect widget IDs should be safe to present here.
 * Railz provides separate IP whitelist capabilities to manage access.
 * For now, will utilize "sandbox" IDs for development and stage env
 */
const RAILZ_CONNECT_WIDGET_ID: { [key in NodeEnvs]: string } = {
  development: 'sb_prod_780a90fd-bac1-470c-94b0-aff6c2c76f44',
  stage: 'sb_prod_780a90fd-bac1-470c-94b0-aff6c2c76f44',
  prod: 'wid_prod_fa4bf70d-60d7-4118-9bd0-85afd0329833',
  'client-uat': 'not_supported',
  client: 'not_supported',
} as const;

const fetchRailzWidgetId = (params: { orgSlug: string }): string => {
  const { orgSlug } = params;
  const env = commonUtils.EnvUtils.getEnv();

  /**
   * This logic is mainly to help drive an override for "AcmeCorp",
   * as we want them to be able to utilize sandbox data (i.e., not "prod").
   * We'll always utilize "dev" if that's the current env, otherwise, default
   * to "stage" for AcmeCorp (even if it's prod) so it has access to sandbox.
   * Otherwise, just utilize the env as is
   */
  if (env === 'development') {
    return RAILZ_CONNECT_WIDGET_ID.development;
  }
  if (orgSlug === 'acmeCorp') {
    return RAILZ_CONNECT_WIDGET_ID.stage;
  }
  return RAILZ_CONNECT_WIDGET_ID[env];
};

const RailzConnectWidget = ({
  organization,
}: {
  organization: { id: string; name: string; slug: string };
}) => {
  const toast = useToast();
  const ref = useRef<HTMLDivElement>(null);

  const [createRailzIntegration] = useCreateRailzIntegrationMutation({
    refetchQueries: [GetActiveRailzConnectionsByOrgIdDocumentGQLType],
    onError: (err) => {
      Sentry.captureException(
        `Create Railz Integration Error: ${JSON.stringify(err, null, 2)}`,
      );

      toast({
        title: 'Error creating Railz Integration record',
        description: commonUtils.EnvUtils.isCustomerFacing()
          ? 'An unexpected error occurred. Please contact support@finleycms.com.'
          : err.message,
        status: 'error',
        position: 'top',
        duration: 6000,
        isClosable: true,
      });
    },
  });

  useEffect(() => {
    const widget = new RailzConnect();

    // Since dev and stage utilize the same Railz environment, we'll want to
    // distinguish the two so adding env to business name to help
    const businessName = commonUtils.EnvUtils.isCustomerFacing()
      ? organization.slug
      : `${commonUtils.EnvUtils.getEnv()}_${organization.slug}`;

    widget.mount({
      parentElement: ref.current,
      widgetId: fetchRailzWidgetId({ orgSlug: organization.slug }),

      // *** Custom Params ***
      // https://docs.railz.ai/docs/railz-connect-parameters
      businessName,
      options: {
        sessionTimeout: 360, // 6 minutes update
      },

      // *** Event Listeners ***
      // https://docs.railz.ai/docs/railz-connect-event-listeners
      onSuccess: async (event: string, data: { [key: string]: any }) => {
        if (event === 'authorization') {
          if (
            !finleyTypeguards.railz.isRailzConnectSuccessAuthorizationDataSchema(
              data,
            )
          ) {
            Sentry.captureException(
              `Invalid Railz Connect onSuccess data structure for "authorization" event: ${JSON.stringify(
                data,
              )} `,
            );
            toast({
              title:
                'An error occurred while creating Railz Integration record. Please contact support@finleycms.com.',
              status: 'error',
              position: 'top',
              duration: 6000,
              isClosable: true,
            });
            return;
          }

          const { businessUuid, connectionUuid, serviceName } = data;
          await createRailzIntegration({
            variables: {
              organizationId: organization.id,
              railzBusinessId: businessUuid,
              railzConnectionId: connectionUuid,
              railzServiceName: serviceName,
            },
          });
        }
      },
      onError: (_event: string, data: { [key: string]: any }) => {
        toast({
          title: 'Error connecting with Railz',
          description: data.error.errorMessage,
          status: 'error',
          position: 'top',
          duration: 6000,
          isClosable: true,
        });
      },
    });
  }, [createRailzIntegration, organization, toast]);

  return <Box id="railz-connect-widget" ref={ref} />;
};

export const RailzConnectModal = ({
  isOpen,
  onClose,
  organizationId,
}: {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
}) => {
  const { data: organizationData } = useGetOrganizationQuery({
    variables: { organizationId },
  });

  if (!organizationData) {
    return <Skeleton />;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent p="3">
        <ModalHeader>Connect to Railz</ModalHeader>
        <ModalCloseButton />
        <ModalBody w="100%">
          <Box maxH="2xl" overflow="auto" rounded="md">
            <RailzConnectWidget organization={organizationData.organization} />
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export const RailzConnectButton = (params: { organizationId: string }) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Sentry.ErrorBoundary
      onError={() => {
        toast({
          title:
            'An error occurred while connecting with Railz. Please contact support@finleycms.com',
          status: 'error',
          position: 'top',
          duration: 6000,
          isClosable: true,
        });
      }}
    >
      <Button onClick={onOpen} rightIcon={<FiPlus />}>
        Connect new account
      </Button>

      <RailzConnectModal
        isOpen={isOpen}
        onClose={onClose}
        organizationId={params.organizationId}
      />
    </Sentry.ErrorBoundary>
  );
};
